import { useTranslation } from 'react-i18next';

import {
  CONTACT_TYPE,
  getChoiceLabel,
  getChoiceList,
  useCategoryChoices,
} from 'services/offers/offerChoices';
import translations from 'services/translations/translations';

// The name of a type of the code in the contacts locale, e.g. customer -> Customer
const localeKey = (type: string) =>
  type.charAt(0).toUpperCase() + type.slice(1);

/**
 * The types of the contacts as the admins have named and ordered them in the choice list,
 * without the hidden ones except those kept (e.g. the types a contact already has). Until the
 * list is loaded, the types of the server with the names of the contacts locale.
 */
const useContactTypeOptions = (
  types: string[],
  kept: string[] = []
): {
  labelOf: (type: string) => string;
  options: { label: string; value: string }[];
} => {
  const { t } = useTranslation([translations.contacts]);
  const { choices, language } = useCategoryChoices();
  const typeChoices = choices.filter((choice) => choice.list === CONTACT_TYPE);

  const fallbackLabel = (type: string) =>
    t(`${translations.contacts}:${localeKey(type)}`, { defaultValue: type });

  const labelOf = (type: string) => {
    const choice = typeChoices.find((option) => option.value === type);
    return choice ? getChoiceLabel(choice, language) : fallbackLabel(type);
  };

  if (typeChoices.length === 0) {
    const all = [...types, ...kept.filter((type) => !types.includes(type))];
    return {
      labelOf,
      options: all.map((type) => ({ label: fallbackLabel(type), value: type })),
    };
  }

  return {
    labelOf,
    options: getChoiceList(choices, CONTACT_TYPE, kept, language),
  };
};

export default useContactTypeOptions;
